import type {
  ClobApiCredentials,
  ClobCredentialsResponse,
  ClobCredentialsStatus,
  ProvisionCredentialsRequest,
  ProvisionCredentialsResponse,
} from '@/types/clob.types';
import axiosClient from './axiosClient';

import { API_ENDPOINTS } from '@/constants';

/**
 * CLOB API Service
 *
 * Handles all interactions with the backend related to
 * Polymarket CLOB (Central Limit Order Book) API credentials.
 *
 * Flow:
 *  1. Frontend calls `provisionCredentials` → Backend derives CLOB API keys
 *     using Privy signer + Polymarket ClobClient.createApiKey()
 *  2. Backend stores them encrypted and returns them to the frontend
 *  3. Frontend can call `getCredentials` to check if credentials exist
 *  4. Frontend uses `setCredentials` to persist new credentials on the backend
 */
export const clobApi = {
  /**
   * Triggers the backend to provision CLOB credentials for the authenticated user.
   *
   * The backend uses the user's EOA and proxy address to call
   * Polymarket's ClobClient.createApiKey() – signing is handled
   * server-side using the stored Privy HMAC builder credentials.
   *
   * @param payload - EOA address and proxy/Safe wallet address
   * @returns The newly provisioned CLOB API credentials
   */
  provisionCredentials: async (
    payload: ProvisionCredentialsRequest,
  ): Promise<ProvisionCredentialsResponse> => {
    const { data } = await axiosClient.post<ProvisionCredentialsResponse>(
      API_ENDPOINTS.CLOB.PROVISION_CREDENTIALS,
      payload,
    );
    return data;
  },

  /**
   * Checks whether CLOB credentials exist for the current user on the backend.
   *
   * @returns Whether credentials exist and the credentials if they do
   */
  getCredentialsStatus: async (): Promise<ClobCredentialsStatus> => {
    const { data } = await axiosClient.get<ClobCredentialsStatus>(
      API_ENDPOINTS.CLOB.CREDENTIALS_STATUS,
    );
    return data;
  },

  /**
   * Retrieves the CLOB credentials stored for the current user.
   * Credentials are encrypted on the backend; this decrypts and returns them.
   *
   * @returns The stored CLOB API credentials
   */
  getCredentials: async (): Promise<ClobCredentialsResponse> => {
    const { data } = await axiosClient.get<ClobCredentialsResponse>(
      API_ENDPOINTS.CLOB.GET_CREDENTIALS,
    );
    return data;
  },

  /**
   * Persists (or updates) CLOB API credentials in the backend secure store.
   * Also updates the legacy SET_CREDENTIALS endpoint for backwards compatibility.
   *
   * @param credentials - The CLOB API key, secret, and passphrase
   */
  setCredentials: async (credentials: ClobApiCredentials): Promise<void> => {
    await axiosClient.post(API_ENDPOINTS.CLOB.SET_CREDENTIALS, credentials);
  },

  /**
   * Deletes the CLOB credentials for the current user.
   * Useful when resetting wallet state.
   */
  deleteCredentials: async (): Promise<{ success: boolean }> => {
    const { data } = await axiosClient.delete<{ success: boolean }>(
      API_ENDPOINTS.CLOB.DELETE_CREDENTIALS,
    );
    return data;
  },
};
